import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Activity } from 'lucide-react';

interface SystemHealthProps {
  health: {
    overall: 'healthy' | 'degraded' | 'down';
    services: Array<{
      name: string;
      status: 'healthy' | 'degraded' | 'down';
      responseTime?: number;
      lastCheck: string;
    }>;
  };
  loading?: boolean;
}

export function SystemHealth({ health, loading }: SystemHealthProps) {
  const getStatusIcon = (status: 'healthy' | 'degraded' | 'down') => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'down':
        return <XCircle className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusColor = (status: 'healthy' | 'degraded' | 'down') => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 dark:text-green-400';
      case 'degraded':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'down':
        return 'text-red-600 dark:text-red-400';
    }
  };

  const getOverallStatusBadge = (status: 'healthy' | 'degraded' | 'down') => {
    const baseClasses = 'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium';
    
    switch (status) {
      case 'healthy':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300`;
      case 'degraded':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300`;
      case 'down':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300`;
    }
  };

  const formatResponseTime = (responseTime?: number): string => {
    if (!responseTime) return 'N/A';
    return `${responseTime}ms`;
  };

  const formatLastCheck = (lastCheck: string): string => {
    try {
      const date = new Date(lastCheck);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      
      if (diffSecs < 60) return `${diffSecs}s ago`;
      if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
      return `${Math.floor(diffSecs / 3600)}h ago`;
    } catch {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/3"></div>
        </div>
        <div className="p-6 space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24"></div>
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            System Health
          </h3>
          <div className="flex items-center space-x-2">
            <Activity className="w-4 h-4 text-gray-400" />
            <span className={getOverallStatusBadge(health.overall)}>
              {health.overall}
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Real-time system component status
        </p>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {health.services.map((service) => (
            <div key={service.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                {getStatusIcon(service.status)}
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {service.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Last check: {formatLastCheck(service.lastCheck)}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-medium ${getStatusColor(service.status)}`}>
                  {service.status}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {formatResponseTime(service.responseTime)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Overall Health Summary */}
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {getStatusIcon(health.overall)}
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Overall System Status
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {health.services.filter(s => s.status === 'healthy').length} of {health.services.length} services healthy
                </div>
              </div>
            </div>
            <div className={`text-lg font-semibold ${getStatusColor(health.overall)}`}>
              {health.overall === 'healthy' ? '✓' : health.overall === 'degraded' ? '⚠' : '✗'}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-4 flex items-center justify-between text-sm">
          <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium">
            View detailed logs →
          </button>
          <span className="text-gray-500 dark:text-gray-400">
            Auto-refresh: 30s
          </span>
        </div>
      </div>
    </div>
  );
}